import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "@jsq/ds/core";
import {
  useCreateIndividualProfileMutation,
  useCreateInstitutionProfileMutation,
  useUpdateControllersCompletedMutation,
  useUpdateIndividualProfileBasicDetailsMutation,
  useUpdateInstitutionProfileMutation,
  useRemoveProfileFromCaseMutation,
} from "../../graphql/supergraph.index";
import useProfile from "../../hooks/profile/useProfile";
import { ControllerIndividualSelection } from "../ControllerIndividualSelection";
import { Controller } from "../ControllerIndividualSelection/types";
import { CreateProfileModal } from "../CreateProfileModal";
import { ProfileFormValues, ProfileType } from "../CreateProfileModal/types";
import { DeleteProfileModal } from "../DeleteProfileModal";
import { ProfileToDelete } from "../DeleteProfileModal/types";
import { ControllersScreenProps, ProfileTypeObject } from "./types";

export const ControllersScreen: React.FC<ControllersScreenProps> = ({
  caseResult,
  basePath,
  arenaId,
}) => {
  const { profileId } = useParams<{ profileId: string }>();
  const history = useHistory();
  const caseId = caseResult?.complianceCasesByObjectId[0].id;
  const profile = useProfile(caseResult);

  // State for the create/edit profile modal
  const [showCreateProfileModal, setShowCreateProfileModal] = useState(false);
  const [showDeleteProfileModal, setShowDeleteProfileModal] = useState(false);
  const [editingControllerId, setEditingControllerId] = useState<string | null>(null);
  const [controllerBeingDeleted, setControllerBeingDeleted] = useState<ProfileToDelete | null>(
    null,
  );

  const [updateControllersCompleted] = useUpdateControllersCompletedMutation({
    onError: () => toast.error("Error marking controllers as completed."),
    onCompleted: () => {
      toast.success("Controllers marked as completed successfully");
    },
    refetchQueries: ["GetComplianceCasesByObjectId"],
    awaitRefetchQueries: true,
  });

  // Get the entity name from the case result
  const entityName = profile?.legalName || "";

  // Helper function to format the name based on compliance object type
  const formatName = (profile: ProfileTypeObject | undefined) => {
    return profile?.complianceObjectType !== "individual"
      ? profile?.legalName || ""
      : `${profile.firstName || ""} ${profile.middleName || ""} ${profile.lastName || ""}`.trim();
  };

  // Transform the data into the format expected by ControllerIndividualSelection
  const controllers: Controller[] =
    profile?.controllingPersons.map((person) => {
      return {
        id: person?.childProfile?.id?.toString() || "",
        name: formatName(person.childProfile),
        type:
          person.childProfile?.complianceObjectType === "individual"
            ? "Individual"
            : "Institution",
        relationship: "Controller",
      };
    }) || [];

  const handleCompleted = async () => {
    await updateControllersCompleted({
      variables: {
        caseId: Number(caseId),
        profileId: Number(profileId),
        controllersCompleted: true,
      },
    });
    history.push(basePath);
  };

  const handleBack = () => {
    // Navigate back to overview
    history.goBack();
  };

  const handleEdit = (id: string) => {
    setEditingControllerId(id);
    setShowCreateProfileModal(true);
  };

  const handleDelete = (id: string) => {
    // Get all the info we'll need to pass to the delete  modal
    const controllerBeingDeletedData = id
      ? profile?.controllingPersons.find((person) => person.childProfile.id.toString() === id)
      : null;

    if (!controllerBeingDeletedData?.childProfile?.id) {
      toast.error("Could not find profile to delete");
      return;
    }

    let isOrHasPaymentBeneficiary = false;
    let descendantNames: string[] = [];

    if (caseResult?.complianceCasesByObjectId[0].profiles) {
      const profileAndDescendants = caseResult?.complianceCasesByObjectId[0].profiles.filter(
        (profile) => profile?.path?.includes(`/${controllerBeingDeletedData.childProfile.id}/`),
      );
      isOrHasPaymentBeneficiary = profileAndDescendants?.some((profile) =>
        profile?.parentRelationships.some(
          (relationship) => relationship.relationshipType === "payment_beneficiary",
        ),
      );
      const re = new RegExp(`\\/${controllerBeingDeletedData.childProfile.id}\\/\\d`);
      descendantNames = profileAndDescendants
        ?.filter((profile) => re.test(profile?.path || ""))
        .map((profile) => formatName(profile as unknown as ProfileTypeObject));
    }

    setControllerBeingDeleted({
      id: controllerBeingDeletedData.childProfile.id.toString(),
      name: formatName(controllerBeingDeletedData.childProfile),
      isOrHasPaymentBeneficiary: isOrHasPaymentBeneficiary,
      descendantNames: descendantNames,
    });
    setShowDeleteProfileModal(true);
  };

  const handleConfirmDelete = async (caseId: number, profileId: string) => {
    await removeProfileFromCase({
      variables: {
        caseId: Number(caseId),
        profileId: Number(profileId),
      },
    });
  };

  // Mutation for creating an individual profile
  const [createIndividualProfile] = useCreateIndividualProfileMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Controller created successfully");
      setShowCreateProfileModal(false);
    },
    refetchQueries: ["GetComplianceCasesByObjectId"],
    awaitRefetchQueries: true,
  });

  const [updateIndividualProfile] = useUpdateIndividualProfileBasicDetailsMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Controller updated successfully");
      setShowCreateProfileModal(false);
    },
    refetchQueries: ["GetComplianceCasesByObjectId"],
    awaitRefetchQueries: true,
  });

  const [updateInstitutionProfile] = useUpdateInstitutionProfileMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Controller updated successfully");
      setShowCreateProfileModal(false);
    },
    refetchQueries: ["GetComplianceCasesByObjectId"],
    awaitRefetchQueries: true,
  });

  const [createInstitutionProfile] = useCreateInstitutionProfileMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Controller created successfully");
      setShowCreateProfileModal(false);
    },
    refetchQueries: ["GetComplianceCasesByObjectId"],
    awaitRefetchQueries: true,
  });

  const [removeProfileFromCase] = useRemoveProfileFromCaseMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Profile(s) deleted");
      setShowDeleteProfileModal(false);
    },
    refetchQueries: ["GetComplianceCasesByObjectId"],
    awaitRefetchQueries: true,
  });

  const editController = async (values: ProfileFormValues) => {
    if (arenaId === undefined) {
      toast.error("An error has occurred, please try again later.");
      return;
    }
    if (!editingControllerId || !editingValues) {
      toast.error("An error has occurred, please try again later.");
      return;
    }
    // Creating a new controller
    if (editingValues.profileType === "Individual") {
      await updateIndividualProfile({
        variables: {
          data: {
            caseId: Number(caseId),
            firstName: values.firstName || editingValues?.firstName || "",
            middleName: values.middleName || editingValues?.middleName || "",
            lastName: values.lastName || editingValues?.lastName || "",
            addresses: [],
            relationshipType: ["controller"],
          },
          profileId: Number(editingControllerId),
        },
      });
    } else {
      await updateInstitutionProfile({
        variables: {
          data: {
            caseId: Number(caseId),
            legalBusinessName:
              values.legalBusinessName || editingValues?.legalBusinessName || "",
            addresses: [],
            relationshipType: ["controller"],
          },
          profileId: Number(editingControllerId),
        },
      });
    }
  };

  const createController = async (values: ProfileFormValues) => {
    if (arenaId === undefined) {
      toast.error("An error has occurred, please try again later.");
      return;
    }

    // Creating a new controller
    if (values.profileType === "Individual") {
      await createIndividualProfile({
        variables: {
          arenaAccessId: Number(arenaId),
          data: {
            caseId: Number(caseId),
            firstName: values.firstName || "",
            middleName: values.middleName,
            lastName: values.lastName || "",
            relationshipType: ["controller"],
            addresses: [],
          },
          parentProfileId: Number(profileId),
        },
      });
    } else {
      await createInstitutionProfile({
        variables: {
          arenaAccessId: Number(arenaId),
          data: {
            caseId: Number(caseId),
            legalBusinessName: values.legalBusinessName || "",
            relationshipType: ["controller"],
            addresses: [],
          },
          parentProfileId: Number(profileId),
        },
      });
    }
  };

  // Find the controller being edited
  const controllerBeingEdited = editingControllerId
    ? profile?.controllingPersons.find(
        (person) => person.childProfile.id.toString() === editingControllerId,
      )
    : null;

  // Prepare initial values for the form when editing
  const editingValues = controllerBeingEdited
    ? {
        profileType: (controllerBeingEdited.childProfile.complianceObjectType === "individual"
          ? "Individual"
          : "Entity") as ProfileType,
        firstName: controllerBeingEdited.childProfile.firstName || "",
        middleName: controllerBeingEdited.childProfile.middleName || "",
        lastName: controllerBeingEdited.childProfile.lastName || "",
        fullName: [
          controllerBeingEdited.childProfile.firstName,
          controllerBeingEdited.childProfile.middleName,
          controllerBeingEdited.childProfile.lastName,
        ]
          .filter(Boolean)
          .join(" "),
        legalBusinessName: controllerBeingEdited.childProfile.legalName || "",
      }
    : undefined;
  return (
    <>
      <ControllerIndividualSelection
        entityName={entityName}
        controllers={controllers}
        onAddController={() => {
          // Reset editing state when adding a new controller
          setEditingControllerId(null);
          setShowCreateProfileModal(true);
        }}
        onCompleted={handleCompleted}
        onBack={handleBack}
        onEdit={handleEdit}
        onDelete={handleDelete}
        readOnly={false}
      />
      <CreateProfileModal
        title={editingControllerId ? "Edit Controller" : "Add Controller"}
        show={showCreateProfileModal}
        showOwnershipPercentage={false}
        profileTypeLabel="Controller"
        initialValues={editingValues}
        onInformationSubmitted={async (values: ProfileFormValues) => {
          if (editingControllerId) {
            await editController(values);
          } else {
            await createController(values);
          }
        }}
        onClose={() => {
          setShowCreateProfileModal(false);
          setEditingControllerId(null);
        }}
      />
      <DeleteProfileModal
        profile={controllerBeingDeleted}
        caseId={Number(caseId)}
        show={showDeleteProfileModal}
        onClose={() => setShowDeleteProfileModal(false)}
        onDelete={handleConfirmDelete}
      />
    </>
  );
};
